import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import * as constants from '../constants'
import {toDateHuman} from './date_helper'
import { orderAmount} from '../share/order_helper'

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const resolution = 72
const mmToPixel = (mm) => resolution/25.4*mm

const billAddress = (order) => {
  let billAddress = order.address || ''

  // find index of zipcode
  const re = /.*\s(\d{4,8}\s.*)/i
  const found = billAddress.match(re);
  if (found) billAddress = billAddress.replace(found[1],`\n${found[1]}`)
  return `${order.client || ''}\n${billAddress}`
}

const thanksText = (order) => {
  let text = `Vielen Dank für Ihre Bestellung.`
  text += "\n"
  if (order.paymentMethod === 'onAccount')
    text += "Wir bitten um Überweisung des Rechnungsbetrages innerhalb von 7 Tagen an die unten genannte Bankverbindung."
  else if(order.paymentMethod === 'paypal')
    text += 'Ihre Bestellung wurde per PayPal bezahlt.'
  else if(order.paymentMethod === 'bankTransfer')
    text += 'Ihre Bestellung wurde per Sofortüberweisung bezahlt.'
  return text
}

const itemsTableBody = (order) => {
  const result = [
    [
      { text: 'Beschreibung', bold: true},
      { text: "Stückpreis", bold: true, alignment: 'right'},
      { text: 'Anzahl', bold: true, alignment: 'right'},
      { text: "Betrag", alignment: 'right', bold: true}
    ]
  ]

  order.items.forEach(item => result.push([
    { text: item.product},
    { text: item.price, alignment: 'right'},
    { text: item.count, alignment: 'right'},
    { text: `${parseFloat(item.price)*parseFloat(item.count)}€`, alignment: 'right', bold: true}
  ]))
  return result
}

const amountTable = (order) => {
  const amount = orderAmount(order,'de-DE')
  const result = []
  result.push([
    '', 'Summe:','', {text: `${amount.nettHuman}€`, alignment: 'right'}
  ])
  if(amount.shippingCosts>0){
    result.push([
      '', 'Versand:','', {text: `+${amount.shippingCostsHuman}€`, alignment: 'right'}
    ])
  }
  if(amount.discount>0){
    result.push([
      '', 'Rabatt:','', {text: `-${amount.discountHuman}€`, alignment: 'right'}
    ])
  }
  result.push([
    '', 'Nettobetrag:','',{text: `${amount.totalNettHuman}€`, alignment: 'right'}
  ])
  result.push([
    '', 'Umsatzsteuer:','', {text: `+${amount.taxHuman}€`, alignment: 'right'}
  ])
  result.push([
    '', {text: 'zu zahlender Betrag:', bold: true},'',
    {text: `${amount.totalGrossHuman}€`, bold: true, alignment: 'right'}
  ])
  return result
}


pdfMake.tableLayouts = {
  items: {
    hLineWidth: () => 0.1,
    vLineWidth: () => 0,
    paddingLeft: (i) => i === 0 ? 0 : 8,
    paddingRight: (i, node) => (i === node.table.widths.length - 1) ? 0 : 8
  },
  amount: {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: (i) => 0,
    paddingRight: (i, node) => 0
  }
}

export const createInvoice = (order, company={}) => {
  const pdfDocGenerator = pdfMake.createPdf({
    pageSize: 'A4',
    pageMargins: [ mmToPixel(16), mmToPixel(20), mmToPixel(16), mmToPixel(20) ],
    content: [
      {
        columns: [
          {
            // auto-sized columns have their widths based on their content
            width: 'auto',
            stack: [
              { text: `${company.name} | ${company.address} | ${company.zipcode} ${company.city}`,
                alignment: 'left',
                decoration: 'underline',
                style: 'small',
                margin: [0, mmToPixel(40), 0, mmToPixel(10)]
              },
              {
                text: billAddress(order), style: 'major'
              }
            ]
          },
          {
            // star-sized columns fill the remaining space
            // if there's more than one star-column, available width is divided equally
            width: '*',
            stack: [
              {
                image: company.logo || constants.LOGO_PLACEHOLDER,
                width: 120,
                alignment: 'right',
                margin: [0, 0, 0, mmToPixel(5)]
              },
              {
                text: `${company.name}\n${company.address}`+
                      `\n${company.zipcode} ${company.city}`+
                      `\n\nE-Mail: ${company.email}`,
                alignment: 'right',
                style: 'default'
              }
            ]

          },
        ]
      },
      {
        margin: [0, mmToPixel(15), 0, 0],
        stack: [
          (order.cancelled ? {
            text: order.cancelled ? 'Storniert' : '',
            bold: true,
            style: 'major',
            color: 'red'
          } : {}),
          { text: `Rechnung: ${order.number}`,
            style: 'major',
            bold: true,
          },
          { text: `Rechnungsdatum: ${toDateHuman(order.date)}`,
            alignment: 'left',
            style: 'default',
          },
        ]
      },
      {
        margin: [0, mmToPixel(10), 0, mmToPixel(5)],
        layout: 'items',
        style: 'default',
        table: {
          // headers are automatically repeated if the table spans over multiple pages
          // you can declare how many rows should be treated as headers
          headerRows: 1,
          widths: [ '*', 'auto', 'auto', 'auto' ],

          body: itemsTableBody(order)
        }
      },
      {
        layout: 'amount',
        style: 'default',
        table: {
          // headers are automatically repeated if the table spans over multiple pages
          // you can declare how many rows should be treated as headers
          headerRows: 1,
          widths: [ '*', mmToPixel(40), mmToPixel(10),'auto'],

          body: amountTable(order)
        }
      },
      {
        margin: [0, mmToPixel(10),0,0],
        text: thanksText(order),
        style: 'default'
      }
    ],
    footer: {
      margin: [mmToPixel(16),0,0,0],
      text: `${company.name} · USt-IDNr. ${company.taxId}\n`+
            `${company.bankName} · BLZ ${company.bankCode} · Konto ${company.bankAccount} · `+
            `IBAN ${company.bankIBAN} · BIC ${company.bankBIC}`,
      style: 'small'
    },

    styles: {
      label: {
        fontSize: 12,
        bold: true
      },
      default: {
        fontSize: 12
      },
      small: {
        fontSize: 9
      },
      major: {
        fontSize: 12
      }
    }
  })
  // return new Promise((resolve) => {
  //   pdfDocGenerator.getDataUrl((data) => resolve({order,data}))
  // })
  return pdfDocGenerator
}



// WEBPACK FOOTER //
// /Users/d064310/Development/playland/colorribbon/src/share/pdf_invoice_generator.js